/**
 * Последняя преграда между отказом запроса и белым экраном.
 *
 * `useQuery` отдаёт отказ броском из рендера, и без границы гаснет всё
 * приложение: React снимает дерево, остаётся пустой `#root` и трасса в
 * консоли. Оверлей Vite сюда не помогает — он ловит сборку и HMR, а не рендер.
 *
 * Ради одного случая, который у живого человека действительно случается:
 * ключ от другого деплоя в localStorage. Он не проверяется на клиенте никак —
 * узнать о нём можно только от сервера, то есть уже броском.
 *
 * Класс, а не хук: границы ошибок в React бывают только классами.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { isNoLearner, reasonOf } from "../lib/errors";
import { NoKey } from "./NoKey";
import { Shell } from "./Shell";

type State = { failed: boolean; error: unknown };

export class Boundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, error };
  }

  // Консоль остаётся источником правды для разработчика: экран объясняет, а
  // трассу не заменяет.
  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    if (isNoLearner(this.state.error)) {
      return (
        <Shell>
          <NoKey reason="unknown" />
        </Shell>
      );
    }
    return (
      <Shell>
        <div className="cols cols--solo">
          <div className="col">
            <div className="eyebrow">Сбой</div>
            <h1 className="h1">Экран не загрузился</h1>
            <div className="notice" style={{ marginTop: 12 }}>{reasonOf(this.state.error)}</div>
            {/* Кнопкой, а не словами. Шапки с навигацией здесь нет — граница
                стоит над всем приложением, — и строка «перезагрузить страницу»
                читалась тупиком: «выйти даже нельзя» (замечание ученика №4,
                30 сентября 2026). */}
            <div className="row" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
                Перезагрузить
              </button>
            </div>
            <p className="muted" style={{ marginTop: 12 }}>
              Если повторяется — напишите взрослому, что делали перед сбоем.
            </p>
          </div>
        </div>
      </Shell>
    );
  }
}
